/**
 * The sound of the show.
 *
 * Everything is synthesised in the Web Audio graph at runtime: the score, the
 * crowd, the stingers. Nothing is streamed or bundled.
 *
 * That is a deliberate production decision, not a shortcut. A 24/7 channel that
 * depends on audio files depends on those files being present, licensed and
 * cached on every machine that ever runs the overlay; a synthesised bed cannot
 * 404 at 4am, adds nothing to the bundle, and can follow the tournament's
 * intensity continuously instead of cross-fading between fixed loops.
 *
 * Signal flow:
 *
 *   music voices ─┐
 *   crowd bed   ──┼─► musicBus ─► duck ─┐
 *   stingers    ──┴─► sfxBus ───────────┴─► master ─► destination
 *
 * `duck` is pulled down whenever the commentator speaks.
 */

export type Mood = 'silent' | 'ambient' | 'tension' | 'battle' | 'epic' | 'victory';

export type SfxName =
  | 'strike'
  | 'critical'
  | 'eliminate'
  | 'whoosh'
  | 'reveal'
  | 'countdown'
  | 'horn'
  | 'fanfare'
  | 'boom'
  | 'heartbeat'
  | 'impact';

interface MoodConfig {
  readonly bpm: number;
  /** Triads as MIDI note numbers, one per bar. */
  readonly progression: readonly (readonly number[])[];
  readonly pad: number;
  readonly bass: number;
  readonly arp: number;
  readonly drums: number;
  readonly crowd: number;
}

const MOODS: Record<Mood, MoodConfig> = {
  silent: { bpm: 90, progression: [[57, 60, 64]], pad: 0, bass: 0, arp: 0, drums: 0, crowd: 0 },
  ambient: {
    bpm: 84,
    progression: [
      [57, 60, 64],
      [53, 57, 60],
      [55, 59, 62],
      [52, 55, 59],
    ],
    pad: 0.5,
    bass: 0.22,
    arp: 0.16,
    drums: 0,
    crowd: 0.1,
  },
  tension: {
    bpm: 100,
    progression: [
      [57, 60, 63],
      [57, 60, 64],
      [56, 59, 63],
      [57, 61, 64],
    ],
    pad: 0.42,
    bass: 0.4,
    arp: 0.3,
    drums: 0.28,
    crowd: 0.2,
  },
  battle: {
    bpm: 128,
    progression: [
      [45, 52, 57],
      [50, 57, 60],
      [43, 50, 55],
      [48, 55, 60],
    ],
    pad: 0.3,
    bass: 0.62,
    arp: 0.44,
    drums: 0.7,
    crowd: 0.34,
  },
  epic: {
    bpm: 140,
    progression: [
      [45, 52, 57],
      [41, 48, 53],
      [43, 50, 55],
      [48, 55, 59],
    ],
    pad: 0.55,
    bass: 0.7,
    arp: 0.6,
    drums: 0.85,
    crowd: 0.5,
  },
  victory: {
    bpm: 120,
    progression: [
      [48, 52, 55],
      [53, 57, 60],
      [55, 59, 62],
      [48, 52, 55],
    ],
    pad: 0.66,
    bass: 0.6,
    arp: 0.7,
    drums: 0.75,
    crowd: 0.75,
  },
};

const LOOKAHEAD_MS = 25;
const SCHEDULE_WINDOW = 0.14;

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private musicBus!: GainNode;
  private duckGain!: GainNode;
  private sfxBus!: GainNode;
  private crowdGain!: GainNode;
  private noiseBuffer!: AudioBuffer;

  private mood: Mood = 'silent';
  private config: MoodConfig = MOODS.silent;
  private nextNoteTime = 0;
  private step = 0;
  private timer: number | null = null;
  private started = false;

  constructor(
    private musicVolume = 0.5,
    private sfxVolume = 0.75,
    private muted = false,
  ) {}

  get isRunning(): boolean {
    return this.started;
  }

  /**
   * Must be called from a user gesture (or in OBS, where autoplay is allowed).
   * Safe to call repeatedly.
   */
  async start(): Promise<void> {
    if (this.started) {
      await this.ctx?.resume();
      return;
    }

    const Ctor: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    const ctx = new Ctor();
    this.ctx = ctx;

    this.master = ctx.createGain();
    this.master.gain.value = this.muted ? 0 : 1;
    this.master.connect(ctx.destination);

    this.duckGain = ctx.createGain();
    this.duckGain.gain.value = 1;
    this.duckGain.connect(this.master);

    this.musicBus = ctx.createGain();
    this.musicBus.gain.value = this.musicVolume;
    this.musicBus.connect(this.duckGain);

    this.sfxBus = ctx.createGain();
    this.sfxBus.gain.value = this.sfxVolume;
    this.sfxBus.connect(this.master);

    this.noiseBuffer = createNoiseBuffer(ctx);
    this.startCrowdBed();

    await ctx.resume();

    this.started = true;
    this.nextNoteTime = ctx.currentTime + 0.08;
    this.timer = window.setInterval(() => this.scheduler(), LOOKAHEAD_MS);
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    void this.ctx?.close();
    this.ctx = null;
    this.started = false;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.ctx) this.rampTo(this.master.gain, muted ? 0 : 1, 0.25);
  }

  setMusicVolume(value: number): void {
    this.musicVolume = Math.min(1, Math.max(0, value));
    if (this.ctx) this.rampTo(this.musicBus.gain, this.musicVolume, 0.3);
  }

  setMood(mood: Mood): void {
    if (this.mood === mood) return;
    this.mood = mood;
    this.config = MOODS[mood];
    if (this.ctx) {
      this.rampTo(this.crowdGain.gain, this.config.crowd * 0.5, 2.4);
    }
  }

  /** Pull the music down while the commentator talks. */
  duck(amount = 0.32, seconds = 1.6): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    const gain = this.duckGain.gain;
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(gain.value, now);
    gain.linearRampToValueAtTime(amount, now + 0.12);
    gain.linearRampToValueAtTime(1, now + Math.max(0.4, seconds));
  }

  /** Crowd swell for a big moment. */
  cheer(intensity = 1): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    const gain = this.crowdGain.gain;
    const peak = Math.min(0.9, this.config.crowd * 0.5 + 0.42 * intensity);
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(gain.value, now);
    gain.linearRampToValueAtTime(peak, now + 0.25);
    gain.linearRampToValueAtTime(this.config.crowd * 0.5, now + 3.2 * intensity);
  }

  play(name: SfxName, intensity = 1): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime;

    switch (name) {
      case 'strike':
        this.tone(t, 620, 300, 0.16, 'triangle', 0.42 * intensity);
        this.noise(t, 0.09, 1800, 0.2 * intensity);
        break;
      case 'critical':
        this.tone(t, 880, 180, 0.34, 'sawtooth', 0.5 * intensity);
        this.tone(t + 0.02, 220, 60, 0.5, 'sine', 0.55 * intensity);
        this.noise(t, 0.22, 2600, 0.34 * intensity);
        break;
      case 'eliminate':
        this.tone(t, 420, 90, 0.42, 'square', 0.28 * intensity);
        this.noise(t, 0.3, 900, 0.24 * intensity);
        break;
      case 'whoosh':
        this.sweepNoise(t, 0.42, 380, 5200, 0.3 * intensity);
        break;
      case 'reveal':
        this.tone(t, 520, 1560, 0.5, 'sine', 0.3 * intensity);
        this.tone(t + 0.06, 780, 2340, 0.4, 'sine', 0.2 * intensity);
        break;
      case 'countdown':
        this.tone(t, 880, 880, 0.14, 'square', 0.34 * intensity);
        break;
      case 'horn':
        this.tone(t, 196, 196, 1.1, 'sawtooth', 0.4 * intensity, 900);
        this.tone(t, 294, 294, 1.1, 'sawtooth', 0.28 * intensity, 900);
        break;
      case 'fanfare':
        [0, 4, 7, 12, 16].forEach((semitone, i) => {
          const freq = midiToFreq(60 + semitone);
          this.tone(t + i * 0.11, freq, freq, 0.7, 'sawtooth', 0.3 * intensity, 2600);
        });
        break;
      case 'boom':
        this.tone(t, 120, 34, 0.85, 'sine', 0.6 * intensity);
        this.noise(t, 0.6, 700, 0.4 * intensity);
        break;
      case 'heartbeat':
        this.tone(t, 70, 46, 0.22, 'sine', 0.5 * intensity);
        this.tone(t + 0.3, 66, 42, 0.26, 'sine', 0.38 * intensity);
        break;
      case 'impact':
        this.tone(t, 180, 40, 0.55, 'sine', 0.55 * intensity);
        this.noise(t, 0.35, 1400, 0.35 * intensity);
        break;
    }
  }

  // ------------------------------------------------------------- synthesis

  private scheduler(): void {
    const ctx = this.ctx;
    if (!ctx) return;

    const secondsPerStep = 60 / this.config.bpm / 4; // sixteenth notes

    while (this.nextNoteTime < ctx.currentTime + SCHEDULE_WINDOW) {
      this.scheduleStep(this.step, this.nextNoteTime, secondsPerStep);
      this.nextNoteTime += secondsPerStep;
      this.step = (this.step + 1) % 64;
    }
  }

  private scheduleStep(step: number, time: number, stepDuration: number): void {
    const cfg = this.config;
    if (cfg.pad === 0 && cfg.drums === 0) return;

    const bar = Math.floor(step / 16) % cfg.progression.length;
    const chord = cfg.progression[bar] ?? cfg.progression[0]!;
    const beat = step % 16;

    // Pad: one long swell per bar.
    if (beat === 0 && cfg.pad > 0) {
      for (const note of chord) {
        const freq = midiToFreq(note + 12);
        this.tone(time, freq, freq, stepDuration * 16, 'triangle', cfg.pad * 0.16, 1400, 0.6);
      }
    }

    // Bass: root on the downbeat and the "and" of three.
    if (cfg.bass > 0 && (beat === 0 || beat === 6 || beat === 10)) {
      const root = midiToFreq((chord[0] ?? 45) - 12);
      this.tone(time, root, root, stepDuration * 3.2, 'sawtooth', cfg.bass * 0.2, 420);
    }

    // Arp: sixteenths climbing the chord.
    if (cfg.arp > 0 && beat % 2 === 0) {
      const note = chord[(beat / 2) % chord.length] ?? chord[0]!;
      const freq = midiToFreq(note + 24);
      this.tone(time, freq, freq, stepDuration * 1.6, 'square', cfg.arp * 0.07, 3200);
    }

    if (cfg.drums > 0) {
      if (beat === 0 || beat === 8) this.kick(time, cfg.drums);
      if (beat === 4 || beat === 12) this.snare(time, cfg.drums);
      if (beat % 2 === 0) this.hat(time, cfg.drums * 0.5);
    }
  }

  private kick(time: number, level: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, time);
    osc.frequency.exponentialRampToValueAtTime(44, time + 0.12);
    gain.gain.setValueAtTime(0.42 * level, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.3);
    osc.connect(gain).connect(this.musicBus);
    osc.start(time);
    osc.stop(time + 0.32);
  }

  private snare(time: number, level: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1900;
    filter.Q.value = 0.8;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.26 * level, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.19);
    source.connect(filter).connect(gain).connect(this.musicBus);
    source.start(time);
    source.stop(time + 0.2);
  }

  private hat(time: number, level: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 7200;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.08 * level, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.06);
    source.connect(filter).connect(gain).connect(this.musicBus);
    source.start(time);
    source.stop(time + 0.07);
  }

  /** One synth note. `endFreq` differing from `freq` gives a pitch sweep. */
  private tone(
    time: number,
    freq: number,
    endFreq: number,
    duration: number,
    type: OscillatorType,
    level: number,
    cutoff = 6000,
    attack = 0.008,
  ): void {
    const ctx = this.ctx;
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, time);
    if (endFreq !== freq) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(20, endFreq), time + duration);
    }

    filter.type = 'lowpass';
    filter.frequency.value = cutoff;

    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, level), time + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);

    const destination = duration > 0.7 && level < 0.25 ? this.musicBus : this.sfxBus;
    osc.connect(filter).connect(gain).connect(destination);
    osc.start(time);
    osc.stop(time + duration + 0.05);
  }

  private noise(time: number, duration: number, cutoff: number, level: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = cutoff;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(level, time);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    source.connect(filter).connect(gain).connect(this.sfxBus);
    source.start(time);
    source.stop(time + duration + 0.02);
  }

  private sweepNoise(
    time: number,
    duration: number,
    fromHz: number,
    toHz: number,
    level: number,
  ): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 1.4;
    filter.frequency.setValueAtTime(fromHz, time);
    filter.frequency.exponentialRampToValueAtTime(toHz, time + duration);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(level, time + duration * 0.4);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    source.connect(filter).connect(gain).connect(this.sfxBus);
    source.start(time);
    source.stop(time + duration + 0.02);
  }

  /** A continuously running filtered-noise bed that reads as a stadium. */
  private startCrowdBed(): void {
    const ctx = this.ctx;
    if (!ctx) return;

    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.loop = true;

    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 760;
    band.Q.value = 0.55;

    const shimmer = ctx.createBiquadFilter();
    shimmer.type = 'lowpass';
    shimmer.frequency.value = 2400;

    // Slow LFO on the band so the crowd breathes instead of hissing.
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.13;
    lfoGain.gain.value = 220;
    lfo.connect(lfoGain).connect(band.frequency);
    lfo.start();

    this.crowdGain = ctx.createGain();
    this.crowdGain.gain.value = 0;

    source.connect(band).connect(shimmer).connect(this.crowdGain).connect(this.musicBus);
    source.start();
  }

  private rampTo(param: AudioParam, value: number, seconds: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    param.linearRampToValueAtTime(value, now + seconds);
  }
}

function midiToFreq(note: number): number {
  return 440 * Math.pow(2, (note - 69) / 12);
}

function createNoiseBuffer(ctx: AudioContext): AudioBuffer {
  const length = ctx.sampleRate * 2;
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  // Pink-ish noise: cheaper than a real filter bank and far less harsh than white.
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  for (let i = 0; i < length; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99765 * b0 + white * 0.099;
    b1 = 0.963 * b1 + white * 0.2965;
    b2 = 0.57 * b2 + white * 1.0526;
    data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.22;
  }
  return buffer;
}
